import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BBALoader from '../BBALoader';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getUserData, CRM_OWNER_EXECUTIVE_ROLES } from '../../services/accessControlService';
import { createBidFromCatalogItem, fetchBidsForAccount } from '../../services/alaCarteBidService';
import BidPackageModal from './BidPackageModal';
import {
  normalizeAlaCarteItem,
  computePricingMetrics,
  PRICING_STATUS as CATALOG_STATUS,
} from '../../utils/alaCarteCatalog';
import {
  BID_STATUS_LABELS,
  PRICING_STATUS,
  CLIENT_RESPONSE_STATUS,
  CLIENT_RESPONSE_LABELS,
  BID_CONTRACT_STATUS,
  BID_CONTRACT_STATUS_LABELS,
  teamQueuePathForBid,
} from '../../utils/alaCarteBid';
import { Send, FileSignature, Layers, Plus, X } from 'lucide-react';

function contractPillClass(status) {
  switch (status) {
    case BID_CONTRACT_STATUS.completed:
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
    case BID_CONTRACT_STATUS.declined:
    case BID_CONTRACT_STATUS.voided:
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    default:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
  }
}

function formatMoney(raw) {
  const n = parseFloat(String(raw));
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  if (!value) return '—';
  if (typeof value.toDate === 'function') return value.toDate().toLocaleDateString();
  if (value instanceof Date) return value.toLocaleDateString();
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? new Date(parsed).toLocaleDateString() : '—';
}

function statusPillClass(status) {
  switch (status) {
    case PRICING_STATUS.published:
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case PRICING_STATUS.awaiting_team:
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100';
    case PRICING_STATUS.team_submitted:
    case PRICING_STATUS.exec_ready:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

/** Status / client-response / contract pills shown on every bid card. */
function BidStatusPills({ bid }) {
  return (
    <span className="flex flex-col items-end gap-1">
      <span
        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          bid ? statusPillClass(bid.pricingStatus) : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
        }`}
      >
        {bid ? BID_STATUS_LABELS[bid.pricingStatus] || bid.pricingStatus : 'Not sent out'}
      </span>
      {bid?.fromProgramSignup === true && bid.pricingStatus !== PRICING_STATUS.published && (
        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
          In signed program — to be priced
        </span>
      )}
      {bid?.clientResponse?.status && (
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            bid.clientResponse.status === CLIENT_RESPONSE_STATUS.accepted
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
              : bid.clientResponse.status === CLIENT_RESPONSE_STATUS.declined
                ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                : 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100'
          }`}
        >
          Client: {CLIENT_RESPONSE_LABELS[bid.clientResponse.status]}
        </span>
      )}
      {bid?.contract?.status && (
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${contractPillClass(bid.contract.status)}`}>
          {BID_CONTRACT_STATUS_LABELS[bid.contract.status] || bid.contract.status}
        </span>
      )}
    </span>
  );
}

/** Pricing, contract, and workspace links for a bid — shared by service cards and package cards. */
function BidDetails({ bid, canSeeInternalPricing, isExec, internalTeam }) {
  const showClientQuote =
    bid.pricingStatus === PRICING_STATUS.exec_ready || bid.pricingStatus === PRICING_STATUS.published;
  const teamPath = teamQueuePathForBid(bid);
  const canManageTeam =
    isExec || (internalTeam && (bid.postedToTeam === internalTeam || bid.department === internalTeam));
  const { marginPercent } = computePricingMetrics(bid.internalCost, bid.clientListPrice);

  return (
    <>
      <dl className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Client quote</dt>
          <dd className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
            {showClientQuote ? formatMoney(bid.clientListPrice) : 'Pending'}
          </dd>
        </div>
        {canSeeInternalPricing && (
          <>
            <div>
              <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Internal cost</dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                {formatMoney(bid.internalCost)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Margin</dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                {marginPercent != null && Number.isFinite(marginPercent) ? `${marginPercent.toFixed(1)}%` : '—'}
              </dd>
            </div>
          </>
        )}
        {bid.pricingStatus === PRICING_STATUS.published && bid.scope && (
          <div className="sm:col-span-3">
            <dt className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Scope</dt>
            <dd className="mt-1 line-clamp-3 text-sm text-gray-700 dark:text-gray-300">{bid.scope}</dd>
          </div>
        )}
      </dl>

      {(bid.contract?.status || bid.assignedPartner) && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {bid.assignedPartner?.companyName && (
            <span className="text-gray-600 dark:text-gray-300">
              Partner: <span className="font-medium">{bid.assignedPartner.companyName}</span>
            </span>
          )}
          {bid.contract?.status === BID_CONTRACT_STATUS.completed && bid.contract?.url && (
            <a
              href={bid.contract.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-medium text-red-600 hover:underline dark:text-red-400"
            >
              <FileSignature className="h-4 w-4" />
              Signed contract (PDF)
            </a>
          )}
        </div>
      )}

      {canManageTeam && (
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          {teamPath && (
            <Link className="font-medium text-red-600 hover:underline dark:text-red-400" to={teamPath}>
              Team workspace
            </Link>
          )}
          {isExec && (
            <Link
              className="font-medium text-red-600 hover:underline dark:text-red-400"
              to={`/crm/alacarte-bid/${bid.id}`}
            >
              Exec review
            </Link>
          )}
        </div>
      )}
    </>
  );
}

/**
 * @param {{ accountId: string, accountType: 'Leads'|'Clients', companyName?: string, active?: boolean }} props
 */
export default function AccountAlaCarteBidsTab({ accountId, accountType, companyName = '', active = true }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bids, setBids] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [internalRole, setInternalRole] = useState(null);
  const [internalTeam, setInternalTeam] = useState(null);
  const [sendingItemId, setSendingItemId] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [packageModal, setPackageModal] = useState(null);

  const isExec = internalRole != null && CRM_OWNER_EXECUTIVE_ROLES.includes(internalRole);

  const canSeeInternalPricing = useMemo(() => {
    if (isExec) return true;
    if (!internalTeam) return false;
    return bids.some((b) => b.postedToTeam === internalTeam || b.department === internalTeam);
  }, [isExec, internalTeam, bids]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [bidList, catalogSnap] = await Promise.all([
        fetchBidsForAccount(accountId),
        getDocs(collection(db, 'alaCarteItems')),
      ]);
      setBids(bidList);
      setCatalogItems(catalogSnap.docs.map((d) => normalizeAlaCarteItem(d.id, d.data())));
    } catch (e) {
      console.error(e);
      setBids([]);
      setCatalogItems([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (!active || !accountId) return;
    load();
  }, [active, accountId, load]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setInternalRole(null);
      setInternalTeam(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getUserData(currentUser.uid);
        if (!cancelled && data) {
          setInternalRole(data.role ?? null);
          setInternalTeam(data.team ?? null);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  /** Latest bid on this account per catalog item — bids arrive newest-first. */
  const latestBidByItemId = useMemo(() => {
    const map = new Map();
    bids.forEach((bid) => {
      if (bid.catalogItemId && !map.has(bid.catalogItemId)) map.set(bid.catalogItemId, bid);
    });
    return map;
  }, [bids]);

  const packageBids = useMemo(() => bids.filter((bid) => bid.isPackage), [bids]);

  /** One-off services typed in by an exec — no catalog item behind them. */
  const customBids = useMemo(() => bids.filter((bid) => !bid.isPackage && !bid.catalogItemId), [bids]);

  /** Catalog item id → the package it was bundled into, so it isn't sent out twice. */
  const packageByItemId = useMemo(() => {
    const map = new Map();
    packageBids.forEach((bid) => {
      bid.packageItems.forEach((item) => {
        if (item.catalogItemId && !map.has(item.catalogItemId)) map.set(item.catalogItemId, bid);
      });
    });
    return map;
  }, [packageBids]);

  const caseByCaseItems = useMemo(
    () =>
      catalogItems
        .filter((item) => item.pricingType === 'caseByCase')
        .filter((item) => item.pricingStatus !== CATALOG_STATUS.unpromoted)
        .sort(
          (a, b) =>
            (a.department || '').localeCompare(b.department || '') || (a.name || '').localeCompare(b.name || '')
        ),
    [catalogItems]
  );

  /** Only services with nothing open on them can be bundled into a new package. */
  const bundleableItems = useMemo(
    () => caseByCaseItems.filter((item) => !latestBidByItemId.has(item.id) && !packageByItemId.has(item.id)),
    [caseByCaseItems, latestBidByItemId, packageByItemId]
  );

  const selectedIds = useMemo(
    () => selectedItemIds.filter((id) => bundleableItems.some((item) => item.id === id)),
    [selectedItemIds, bundleableItems]
  );

  const toggleSelected = (itemId) => {
    setSelectedItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleSendForBid = async (catalogItemId) => {
    if (!isExec || !accountId || sendingItemId) return;
    setSendingItemId(catalogItemId);
    try {
      await createBidFromCatalogItem({
        accountId,
        catalogItemId,
        source: 'crm',
        postedByUid: currentUser?.uid || '',
        postToTeam: true,
      });
      await load();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to send out for bid');
    } finally {
      setSendingItemId('');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <BBALoader size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isExec && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {selectedIds.length > 0
              ? `${selectedIds.length} ${selectedIds.length === 1 ? 'service' : 'services'} selected`
              : 'Select services below to quote them together as one package.'}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedItemIds([])}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => setPackageModal({ customOnly: true, preselected: [] })}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Plus className="h-4 w-4" />
              Custom package
            </button>
            <button
              type="button"
              disabled={selectedIds.length === 0}
              onClick={() => setPackageModal({ customOnly: false, preselected: selectedIds })}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              <Layers className="h-4 w-4" />
              {selectedIds.length > 0 ? `Combine ${selectedIds.length} into a package` : 'Combine into a package'}
            </button>
          </div>
        </div>
      )}

      {packageBids.length > 0 && (
        <section className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <h3 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">Bid packages</h3>
          <ul className="grid gap-4 lg:grid-cols-2">
            {packageBids.map((bid) => (
              <li
                key={bid.id}
                className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{bid.icon || '🧩'}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{bid.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {bid.department} · {bid.packageItems.length}{' '}
                        {bid.packageItems.length === 1 ? 'service' : 'services'} · Sent out{' '}
                        {formatDate(bid.createdAt)}
                      </p>
                    </div>
                  </div>
                  <BidStatusPills bid={bid} />
                </div>

                <ul className="mt-4 space-y-1">
                  {bid.packageItems.map((item, index) => (
                    <li
                      key={`${bid.id}-${item.catalogItemId || 'custom'}-${index}`}
                      className="flex items-baseline gap-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      <span className="leading-none">{item.icon || '📦'}</span>
                      <span>
                        {item.quantity > 1 ? `${item.quantity} × ` : ''}
                        {item.name}
                        {item.notes ? (
                          <span className="text-gray-500 dark:text-gray-400"> — {item.notes}</span>
                        ) : null}
                      </span>
                      {item.custom && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          Custom
                        </span>
                      )}
                    </li>
                  ))}
                </ul>

                <BidDetails
                  bid={bid}
                  canSeeInternalPricing={canSeeInternalPricing}
                  isExec={isExec}
                  internalTeam={internalTeam}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {customBids.length > 0 && (
        <section className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <h3 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">Custom services</h3>
          <ul className="grid gap-4 lg:grid-cols-2">
            {customBids.map((bid) => (
              <li
                key={bid.id}
                className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{bid.icon || '✏️'}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{bid.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {bid.department} · Created {formatDate(bid.createdAt)}
                      </p>
                    </div>
                  </div>
                  <BidStatusPills bid={bid} />
                </div>
                {bid.description && (
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{bid.description}</p>
                )}
                <BidDetails
                  bid={bid}
                  canSeeInternalPricing={canSeeInternalPricing}
                  isExec={isExec}
                  internalTeam={internalTeam}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
        {(packageBids.length > 0 || customBids.length > 0) && (
          <h3 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
            Individual services
          </h3>
        )}
        {caseByCaseItems.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-300 py-12 text-center text-gray-500 dark:border-gray-600 dark:text-gray-400">
            No case-by-case services in the catalog.
          </p>
        ) : (
          <ul className="grid gap-4 lg:grid-cols-2">
            {caseByCaseItems.map((item) => {
              const bid = latestBidByItemId.get(item.id) || null;
              const inPackage = packageByItemId.get(item.id) || null;
              const selectable = isExec && !bid && !inPackage;
              const selected = selectedIds.includes(item.id);
              // Picked in the program the client signed but kept off the contract because it
              // hadn't been priced yet — gold until a price is published.
              const signupToBePriced =
                bid?.fromProgramSignup === true && bid.pricingStatus !== PRICING_STATUS.published;

              return (
                <li
                  key={item.id}
                  className={`flex flex-col rounded-xl border p-5 shadow-sm ${
                    selected
                      ? 'border-red-400 bg-white ring-1 ring-red-200 dark:border-red-700 dark:bg-gray-900/30 dark:ring-red-900/40'
                      : signupToBePriced
                        ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-300 dark:border-amber-500 dark:bg-amber-900/20 dark:ring-amber-600/50'
                        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900/30'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {selectable && (
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelected(item.id)}
                          aria-label={`Select ${item.name} for a package`}
                          className="mt-1.5 h-4 w-4 shrink-0 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      )}
                      <span className="text-3xl">{item.icon || '📦'}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{item.name}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {item.department}
                          {bid ? ` · Sent out ${formatDate(bid.createdAt)}` : ''}
                        </p>
                      </div>
                    </div>
                    {inPackage && !bid ? (
                      <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">
                        In package
                      </span>
                    ) : (
                      <BidStatusPills bid={bid} />
                    )}
                  </div>

                  {bid ? (
                    <BidDetails
                      bid={bid}
                      canSeeInternalPricing={canSeeInternalPricing}
                      isExec={isExec}
                      internalTeam={internalTeam}
                    />
                  ) : inPackage ? (
                    <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                      Quoted as part of{' '}
                      <span className="font-medium">
                        {inPackage.icon} {inPackage.name}
                      </span>
                      .
                    </p>
                  ) : (
                    <div className="mt-4 flex flex-1 flex-col justify-end">
                      {isExec ? (
                        <button
                          type="button"
                          disabled={sendingItemId === item.id}
                          onClick={() => handleSendForBid(item.id)}
                          className="inline-flex items-center justify-center gap-2 self-start rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          <Send className="h-4 w-4" />
                          {sendingItemId === item.id ? 'Sending…' : 'Send out for bid'}
                        </button>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No bid requested for {companyName || (accountType === 'Clients' ? 'this client' : 'this lead')} yet.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {packageModal && (
        <BidPackageModal
          accountId={accountId}
          companyName={companyName}
          catalogItems={bundleableItems}
          preselectedItemIds={packageModal.preselected}
          customOnly={packageModal.customOnly}
          onClose={() => setPackageModal(null)}
          onCreated={async () => {
            setPackageModal(null);
            setSelectedItemIds([]);
            await load();
          }}
        />
      )}
    </div>
  );
}
